import { useEffect, useState } from 'react';
import { usePageTitle } from '../../hooks/usePageTitle';
import './index.css';
import SideBar from '../../components/global/SideBar';
import { ErrorModal, Loader, PasswordInput, SuccessModal, useCurrentUser, type UserInterface } from '@beholos/holoscommon';
import { updatePassword, updateProfileInformation } from '../../services/account.service';

export default function Account() {
  usePageTitle('Mi Perfil - Holos');
  const [loading, setLoading] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');
  const [successModalMessage, setSuccessModalMessage] = useState('');
  const [activeTab, setActiveTab] = useState<'profile' | 'password'>('profile');
  const [passwordData, setPasswordData] = useState({
    oldpassword: '',
    password: '',
    passwordconf: ''
  });
  const [updatedUser, setUpdatedUser] = useState<UserInterface | null>(null);

  const { user, loading: loadingUser, refetch } = useCurrentUser();

  useEffect(() => {
    if (user) {
      console.error('User data is not available');
      setUpdatedUser(user);
    }
  }, [user]);

  const submitProfileUpdate = (e: React.FormEvent) => {
    e.preventDefault();

    if (!updatedUser) {
      alert('No user data available to update');
      return;
    }

    console.log('Profile update request:', updatedUser);
    setLoading(true);
    updateProfileInformation({
      firstname: updatedUser.firstname,
      lastname: updatedUser.lastname,
      nationalIdentityNumber: updatedUser.nationalIdentityNumber,
      phoneNumber: updatedUser.phoneNumber,
      countryCode: updatedUser.countryCode,
      birthDate: updatedUser.birthDate,
      gender: updatedUser.gender,
      address: updatedUser.address
    }).then((message) => {
      refetch();
      if (message === 'Perfil actualizado con éxito') {
        setSuccessModalMessage(message);
      } else {
        setErrorModalMessage(message);
      }
    }).catch((error: any) => {
      console.error('Error updating profile information:', error);
      setErrorModalMessage('Algo salió mal al actualizar la información del perfil');
    }).finally(() => {
      setLoading(false);
    });
  }

  const submitPasswordChange = (e: React.FormEvent) => {
    e.preventDefault();

    if (!passwordData.oldpassword || !passwordData.password || !passwordData.passwordconf) {
      setErrorModalMessage("Por favor complete todos los campos");
      return;
    }

    if (passwordData.password !== passwordData.passwordconf) {
      setErrorModalMessage("Las contraseñas no coinciden");
      return;
    }

    if (passwordData.password.length < 8 || !/[A-Za-z]/.test(passwordData.password) || !/[0-9]/.test(passwordData.password)) {
      setErrorModalMessage("La contraseña debe tener al menos 8 caracteres y contener al menos una letra y un número");
      return;
    }

    console.log('Password change request:', {
      oldpassword: passwordData.oldpassword,
      password: passwordData.password,
      passwordconf: passwordData.passwordconf
    });

    setLoading(true)
    updatePassword(passwordData.oldpassword, passwordData.password).then(() => {
      setSuccessModalMessage('Contraseña actualizada exitosamente');
    }).catch((error) => {
      console.error('Error updating password:', error);
      setErrorModalMessage('Algo salió mal al actualizar la contraseña');
    }).finally(() => {
      setLoading(false);
      setPasswordData({ oldpassword: '', password: '', passwordconf: '' });
    });

  };

  const handlePasswordInputChange = (field: string, value: string) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="flex h-[calc(100vh-80px)] w-screen overflow-hidden">
      <div className="hidden md:block md:w-80 lg:w-96 h-full overflow-y-auto">
        <SideBar />
      </div>

      <div className="flex-1 h-full overflow-y-auto bg-gray-200 py-15 px-6">
        {loadingUser ? <Loader /> : <div className="bg-white p-6 rounded max-w-6xl mx-auto border border-gray-200 shadow-md">

          <h3 className="text-3xl md:text-4xl mb-3 font-bold font-juana text-left">
            <span>{user?.firstname} {user?.lastname}</span>
          </h3>

          <ul className="flex gap-6 mb-6 text-sm font-medium font-juana">
            <li>
              <button
                className={
                  activeTab === 'profile'
                    ? 'text-holos-dark border-b-2 border-holos-dark pb-1 cursor-pointer'
                    : 'text-gray-600 hover:text-gray-800 cursor-pointer'
                }
                onClick={() => setActiveTab('profile')}
              >
                Editar perfil
              </button>
            </li>
            <li>
              <button
                className={
                  activeTab === 'password'
                    ? 'text-holos-dark border-b-2 border-holos-dark pb-1 cursor-pointer'
                    : 'text-gray-600 hover:text-gray-800 cursor-pointer'
                }
                onClick={() => setActiveTab('password')}
              >
                Cambiar contraseña
              </button>
            </li>
          </ul>

          {activeTab === 'profile' && (
            <div className="space-y-6">
              <section className="bg-white border border-gray-200 rounded-2xl py-6 px-6 my-6">
                <h2 className="text-xl mb-4 font-medium">Datos personales</h2>

                <form onSubmit={submitProfileUpdate} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block mb-1 font-bold">Nombres:</label>
                      <input
                        type="text"
                        name="firstname"
                        value={updatedUser?.firstname || ''}
                        onChange={(e) => setUpdatedUser(prev => prev ? { ...prev, firstname: e.target.value } : prev)}
                        required
                        className="w-full px-4 py-2 border border-black rounded-full focus:outline-none focus:ring-2 focus:ring-holos-dark"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 font-bold">Apellidos:</label>
                      <input
                        type="text"
                        name="lastname"
                        value={updatedUser?.lastname || ''}
                        onChange={(e) => setUpdatedUser(prev => prev ? { ...prev, lastname: e.target.value } : prev)}
                        required
                        className="w-full px-4 py-2 border border-black rounded-full focus:outline-none focus:ring-2 focus:ring-holos-dark"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block mb-1 font-bold">DNI:</label>
                      <input
                        type="text"
                        name="nationalIdentityNumber"
                        value={updatedUser?.nationalIdentityNumber || ''}
                        onChange={(e) => setUpdatedUser(prev => prev ? { ...prev, nationalIdentityNumber: Number(e.target.value) } : prev)}
                        required
                        className="w-full px-4 py-2 border border-black rounded-full focus:outline-none focus:ring-2 focus:ring-holos-dark"
                      />
                    </div>

                    <div>
                      <label className="block mb-1 font-bold">Teléfono:</label>
                      <div className="flex flex-col md:flex-row md:items-center gap-2">
                        <select
                          className="w-full md:w-40 md:mr-2 px-4 py-2 border border-black rounded-full focus:outline-none focus:ring-2 focus:ring-holos-dark"
                          name="country"
                          onChange={(e) => setUpdatedUser(prev => prev ? { ...prev, countryCode: e.target.value } : prev)}
                          value={updatedUser?.countryCode || ''}>
                          <option value="" disabled>
                            Seleccionar país
                          </option>
                          <option value="+54">Argentina: +54</option>
                          <option value="+501">Belize: +501</option>
                          <option value="+591">Bolivia: +591</option>
                          <option value="+55">Brazil: +55</option>
                          <option value="+56">Chile: +56</option>
                          <option value="+57">Colombia: +57</option>
                          <option value="+506">Costa Rica: +506</option>
                          <option value="+53">Cuba: +53</option>
                          <option value="+1-809">Dominican Republic: +1-809</option>
                          <option value="+1-829">Dominican Republic: +1-829</option>
                          <option value="+1-849">Dominican Republic: +1-849</option>
                          <option value="+593">Ecuador: +593</option>
                          <option value="+503">El Salvador: +503</option>
                          <option value="+502">Guatemala: +502</option>
                          <option value="+504">Honduras: +504</option>
                          <option value="+52">Mexico: +52</option>
                          <option value="+505">Nicaragua: +505</option>
                          <option value="+507">Panama: +507</option>
                          <option value="+595">Paraguay: +595</option>
                          <option value="+51">Peru: +51</option>
                          <option value="+1-787">Puerto Rico: +1-787</option>
                          <option value="+1-939">Puerto Rico: +1-939</option>
                          <option value="+1">United States/Canada: +1</option>
                          <option value="+598">Uruguay: +598</option>
                          <option value="+58">Venezuela: +58</option>
                        </select>
                        <input
                          type="tel"
                          name="phone"
                          value={updatedUser?.phoneNumber || ''}
                          onChange={(e) => setUpdatedUser(prev => prev ? { ...prev, phoneNumber: e.target.value } : prev)}
                          className="w-full md:flex-1 px-4 py-2 border border-black rounded-full focus:outline-none focus:ring-2 focus:ring-holos-dark"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block mb-1 font-bold">Fecha de nacimiento:</label>
                      <input
                        type="date"
                        name="birthDate"
                        value={updatedUser?.birthDate || ''}
                        onChange={(e) => setUpdatedUser(prev => prev ? { ...prev, birthDate: e.target.value } : prev)}
                        min="1925-01-01"
                        max="2025-07-10"
                        className="w-full px-4 py-2 border border-black rounded-full focus:outline-none focus:ring-2 focus:ring-holos-dark"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 font-bold">Email:</label>
                      <input
                        type="email"
                        name="email"
                        defaultValue={updatedUser?.email || ''}
                        disabled
                        className="w-full px-4 py-2 border border-black rounded-full bg-gray-100 cursor-not-allowed focus:outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block mb-2 font-bold">Sexo</label>
                    <div className="flex items-center gap-6 text-sm">
                      <label className="inline-flex items-center cursor-pointer" htmlFor="male">
                        <input
                          type="radio"
                          name="gender"
                          value="male"
                          id="male"
                          onChange={(e) => setUpdatedUser(prev => prev ? { ...prev, gender: e.target.value } : prev)}
                          checked={updatedUser?.gender === 'male'}
                          className="form-radio text-black accent-black h-6 w-6"
                        />
                        <span className="ml-2">Masculino</span>
                      </label>
                      <label className="inline-flex items-center cursor-pointer" htmlFor="female">
                        <input
                          type="radio"
                          name="gender"
                          value="female"
                          id="female"
                          onChange={(e) => setUpdatedUser(prev => prev ? { ...prev, gender: e.target.value } : prev)}
                          checked={updatedUser?.gender === 'female'}
                          className="form-radio text-black accent-black h-6 w-6"
                        />
                        <span className="ml-2">Femenino</span>
                      </label>
                    </div>
                  </div>

                  <h2 className="text-xl mb-4 font-medium">Mi dirección</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block mb-1 font-bold">Calle:</label>
                      <input
                        type="text"
                        name="addressStreet"
                        value={updatedUser?.address.street || ''}
                        onChange={(e) => setUpdatedUser(prev => prev ? { ...prev, address: { ...prev.address, street: e.target.value } } : prev)}
                        className="w-full px-4 py-2 border border-black rounded-full focus:outline-none focus:ring-2 focus:ring-holos-dark"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 font-bold">Distrito:</label>
                      <input
                        type="text"
                        name="addressState"
                        value={updatedUser?.address.state || ''}
                        onChange={(e) => setUpdatedUser(prev => prev ? { ...prev, address: { ...prev.address, state: e.target.value } } : prev)}
                        className="w-full px-4 py-2 border border-black rounded-full focus:outline-none focus:ring-2 focus:ring-holos-dark"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block mb-1 font-bold">Ciudad:</label>
                      <input
                        type="text"
                        name="addressCity"
                        value={updatedUser?.address.city || ''}
                        onChange={(e) => setUpdatedUser(prev => prev ? { ...prev, address: { ...prev.address, city: e.target.value } } : prev)}
                        className="w-full px-4 py-2 border border-black rounded-full focus:outline-none focus:ring-2 focus:ring-holos-dark"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 font-bold">País:</label>
                      <select
                        className="w-full px-4 py-2 border border-black rounded-full focus:outline-none focus:ring-2 focus:ring-holos-dark"
                        name="country"
                        value={updatedUser?.address.countryCode || ''}
                        onChange={(e) => setUpdatedUser(prev => prev ? { ...prev, address: { ...prev.address, countryCode: e.target.value } } : prev)}
                      >
                        <option value="" disabled>
                          Seleccionar país
                        </option>
                        <option value="Argentina">Argentina</option>
                        <option value="Belice">Belice</option>
                        <option value="Bolivia">Bolivia</option>
                        <option value="Brasil">Brasil</option>
                        <option value="Canadá">Canadá</option>
                        <option value="Chile">Chile</option>
                        <option value="Colombia">Colombia</option>
                        <option value="Costa Rica">Costa Rica</option>
                        <option value="Cuba">Cuba</option>
                        <option value="Ecuador">Ecuador</option>
                        <option value="El Salvador">El Salvador</option>
                        <option value="Estados Unidos">Estados Unidos</option>
                        <option value="Guatemala">Guatemala</option>
                        <option value="Honduras">Honduras</option>
                        <option value="México">México</option>
                        <option value="Nicaragua">Nicaragua</option>
                        <option value="Panamá">Panamá</option>
                        <option value="Paraguay">Paraguay</option>
                        <option value="Perú">Perú</option>
                        <option value="Puerto Rico">Puerto Rico</option>
                        <option value="República Dominicana">República Dominicana</option>
                        <option value="Uruguay">Uruguay</option>
                        <option value="Venezuela">Venezuela</option>
                      </select>
                    </div>
                  </div>

                  <button type="submit" className="bg-black text-white border border-black px-6 py-2 rounded-lg cursor-pointer">
                    Guardar
                  </button>
                </form>
              </section>
            </div>
          )}

          {activeTab === 'password' && (
            <section className="bg-white border border-gray-200 rounded-2xl py-6 px-6 my-6">
              {loading ? <Loader /> : <div style={{ maxWidth: '500px' }}>
                <h2 className="text-xl mb-4 font-medium">Actualizar contraseña</h2>

                <form onSubmit={submitPasswordChange} className="space-y-6">
                  <div>
                    <label className="block mb-1 font-bold">Contraseña actual</label>
                    <PasswordInput
                      value={passwordData.oldpassword}
                      onChange={(value) => handlePasswordInputChange('oldpassword', value)}
                    />
                  </div>

                  <div>
                    <label className="block mb-1 font-bold">Contraseña nueva</label>
                    <PasswordInput
                      value={passwordData.password}
                      onChange={(value) => handlePasswordInputChange('password', value)}
                    />
                    <div className="text-gray-500 mt-2 text-sm">
                      La contraseña debe tener al menos 8 caracteres, una letra y un número
                    </div>
                  </div>

                  <div>
                    <label className="block mb-1 font-bold">Confirmar Contraseña nueva</label>
                    <PasswordInput
                      value={passwordData.passwordconf}
                      onChange={(value) => handlePasswordInputChange('passwordconf', value)}
                    />
                  </div>

                  <button type="submit" className="bg-black text-white border border-black px-6 py-2 rounded-lg cursor-pointer">
                    Guardar
                  </button>
                </form>
              </div>}
            </section>
          )}
        </div>}

        <ErrorModal
          title=""
          onClose={() => setErrorModalMessage('')}
          isOpen={!!errorModalMessage}
          message={errorModalMessage} />

        <SuccessModal
          onClose={() => setSuccessModalMessage('')}
          isOpen={!!successModalMessage}
          message={successModalMessage}
        />
      </div>
    </div>
  );
}
